import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Divider,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import {
  MonetizationOn as CoinIcon,
  Settings as SettingsIcon,
  TrendingUp as XPIcon,
  Layers as LimitsIcon,
  AttachMoney as FeeIcon,
  CardGiftcard as PackagesIcon,
  Subscriptions as SubscriptionIcon,
  Build as SystemIcon,
  TuneOutlined as AdvancedIcon,
} from "@mui/icons-material";
import useAdminContext from "../../store/contexts/AdminContext";
import CoinEconomySettings from "./CoinEconomySettings";
import RoomLimitsSettings from "./RoomLimitsSettings";
import XPRewardsSettings from "./XPRewardsSettings";
import PlatformBasicSettings from "./PlatformBasicSettings";
import FeeLimitsSettings from "./FeeLimitsSettings";
import CoinPackagesSettings from "./CoinPackagesSettings";
import SubscriptionPlansSettings from "./SubscriptionPlansSettings";
import SystemConfigSettings from "./SystemConfigSettings";
import AdvancedSettings from "./AdvancedSettings";
import { ADMIN_ROLES } from "../../constants/types";

const PlatformEconomySettings = () => {
  const history = useHistory();
  const {
    admin,
    authState,
    showSnackbar,
    getCoinEconomyApiCall,
    updateCoinEconomyApiCall,
    getRoomLimitsApiCall,
    updateRoomLimitsApiCall,
    getXPRewardsApiCall,
    updateXPRewardsApiCall,
    getGeneralSettingsApiCall,
    updateGeneralSettingsApiCall,
    getFeeLimitsApiCall,
    updateFeeLimitsApiCall,
  } = useAdminContext();
  const { loading } = authState;

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [accessDenied, setAccessDenied] = useState(false);
  const [settings, setSettings] = useState({
    coinEconomy: {},
    roomLimits: {},
    xpRewards: {},
    platformBasic: {},
    feeLimits: {},
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // ============= RBAC HELPER =============
  const isSuperAdmin = useCallback(() => {
    return admin?.role === ADMIN_ROLES.SUPER_ADMIN;
  }, [admin?.role]);

  // ============= LOAD SETTINGS =============
  const loadAllSettings = useCallback(async () => {
    if (!isSuperAdmin()) return;

    setLoadingSettings(true);
    try {
      const [coinEconomy, roomLimits, xpRewards, generalSettings, feeLimits] =
        await Promise.all([
          getCoinEconomyApiCall(),
          getRoomLimitsApiCall(),
          getXPRewardsApiCall(),
          getGeneralSettingsApiCall(),
          getFeeLimitsApiCall(),
        ]);

      setSettings({
        coinEconomy: coinEconomy || {},
        roomLimits: roomLimits || {},
        xpRewards: xpRewards || {},
        platformBasic: generalSettings || {},
        feeLimits: feeLimits || {},
      });
    } catch (error) {
      console.error("Error loading economy settings:", error);
      showSnackbar("Failed to load economy settings", "error");
    } finally {
      setLoadingSettings(false);
    }
  }, [
    isSuperAdmin,
    getCoinEconomyApiCall,
    getRoomLimitsApiCall,
    getXPRewardsApiCall,
    getGeneralSettingsApiCall,
    getFeeLimitsApiCall,
    showSnackbar,
  ]);

  // ============= HANDLE SETTING CHANGE =============
  const handleSettingChange = useCallback((category, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value,
      },
    }));
    setHasUnsavedChanges(true);
  }, []);

  // ============= SAVE SETTINGS =============
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const savePromises = [];

      // Map active tab to save function
      const saveMapping = {
        0: {
          data: settings.coinEconomy,
          fn: updateCoinEconomyApiCall,
          name: "Coin Economy",
        },
        1: {
          data: settings.roomLimits,
          fn: updateRoomLimitsApiCall,
          name: "Room Limits",
        },
        2: {
          data: settings.xpRewards,
          fn: updateXPRewardsApiCall,
          name: "XP Rewards",
        },
        3: {
          data: settings.platformBasic,
          fn: updateGeneralSettingsApiCall,
          name: "General Settings",
        },
        4: {
          data: settings.feeLimits,
          fn: updateFeeLimitsApiCall,
          name: "Fee Limits",
        },
      };

      const currentTab = saveMapping[activeTab];

      if (currentTab && currentTab.fn) {
        await currentTab.fn(currentTab.data);
        showSnackbar(`${currentTab.name} saved successfully!`, "success");
        setHasUnsavedChanges(false);
      } else {
        showSnackbar("Save not available for this tab", "info");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      showSnackbar("Failed to save settings. Please try again.", "error");
    } finally {
      setSavingSettings(false);
    }
  };

  // ============= RBAC PROTECTION - COMPONENT LEVEL =============
  useEffect(() => {
    // Extra layer of protection - redirect if not super admin
    if (admin && !isSuperAdmin()) {
      setAccessDenied(true);
      showSnackbar(
        "Access Denied: Only super admins can access platform economy settings",
        "error"
      );

      // Redirect after 2 seconds
      const timer = setTimeout(() => {
        history.push("/admin/dashboard");
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [admin, isSuperAdmin, history, showSnackbar]);

  // ============= LOAD SETTINGS ON MOUNT =============
  useEffect(() => {
    if (admin && isSuperAdmin()) {
      loadAllSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Tab configuration
  const tabs = [
    {
      label: "Coin Economy",
      icon: <CoinIcon />,
      component: (
        <CoinEconomySettings
          settings={settings}
          onSettingChange={handleSettingChange}
        />
      ),
      canSave: true,
    },
    {
      label: "Room Limits",
      icon: <LimitsIcon />,
      component: (
        <RoomLimitsSettings
          settings={settings}
          onSettingChange={handleSettingChange}
        />
      ),
      canSave: true,
    },
    {
      label: "XP Rewards",
      icon: <XPIcon />,
      component: (
        <XPRewardsSettings
          settings={settings}
          onSettingChange={handleSettingChange}
        />
      ),
      canSave: true,
    },
    {
      label: "Basic Settings",
      icon: <SettingsIcon />,
      component: (
        <PlatformBasicSettings
          settings={settings}
          onSettingChange={handleSettingChange}
        />
      ),
      canSave: true,
    },
    {
      label: "Fee Limits",
      icon: <FeeIcon />,
      component: (
        <FeeLimitsSettings
          settings={settings}
          onSettingChange={handleSettingChange}
        />
      ),
      canSave: true,
    },
    {
      label: "Coin Packages",
      icon: <PackagesIcon />,
      component: <CoinPackagesSettings />,
      canSave: false,
    },
    {
      label: "Subscriptions",
      icon: <SubscriptionIcon />,
      component: <SubscriptionPlansSettings />,
      canSave: false,
    },
    {
      label: "System Config",
      icon: <SystemIcon />,
      component: <SystemConfigSettings />,
      canSave: false,
    },
    {
      label: "Advanced",
      icon: <AdvancedIcon />,
      component: <AdvancedSettings />,
      canSave: false,
    },
  ];

  // Show access denied message
  if (accessDenied || (admin && !isSuperAdmin())) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1">
            Only super admins can access platform economy settings. You will be
            redirected to the dashboard shortly.
          </Typography>
        </Alert>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  // Show loading state while checking admin role
  if (loading || !admin) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          color="text.primary"
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          Platform Economy Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure platform-wide economy settings including coins, XP, rewards,
          and monetization parameters.
        </Typography>
      </Box>

      {/* Security Notice */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Important:</strong> Changes to these settings affect the
          entire platform economy. Please review carefully before saving.
        </Typography>
      </Alert>

      {/* Tabs Navigation */}
      <Paper sx={{ mb: 3 }} elevation={2}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              minHeight: 64,
              textTransform: "none",
              fontWeight: 500,
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Paper sx={{ p: 3 }} elevation={2}>
        {loadingSettings ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          tabs[activeTab].component
        )}

        {/* Save Button - Show only for tabs that support saving */}
        {tabs[activeTab].canSave && !loadingSettings && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={savingSettings ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveSettings}
                disabled={!hasUnsavedChanges || savingSettings}
                sx={{
                  minWidth: 150,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {savingSettings ? "Saving..." : "Save Changes"}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PlatformEconomySettings;
